import { css } from "@linaria/core"
import { styled } from "@linaria/react"
import { runInAction } from "mobx"
import { observer } from "mobx-react-lite"
import { useEffect, useMemo, useRef, useState } from "react"


import { LoadMoreButton } from "@/components/Chat/LoadMoreButton.tsx"
import { MessageText } from "@/components/Chat/MessageStyles.ts"
import { StyledUserMessageBalloon, UserMessageContainer, UserMessageType } from "@/components/Chat/UserMessageStyles.ts"
import { Gap, Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { createLLMLogChatModel } from "@/components/LLMLogChat/LLMLogChatModel.ts"
import { LLMLogPagesList } from "@/components/LLMLogChat/LLMLogPagesList.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { usePersistence, useQueries } from "@/hooks/useRootContext.ts"
import { formatDateTime, formatDuration } from "@/lib/date.ts"
import { creditsToDollars, formatDollars } from "@/lib/displayUtils.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { Characters, ellipsize } from "@/styles/textStyles.ts"

import type { LLMLogChatModel, LLMLogModel } from "@/components/LLMLogChat/LLMLogChatModel.ts"
import type { ScrollableModel } from "@/components/Scrollable/ScrollableModel.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import type { RefObject } from "react"

export const LLMLogChat = observer((props: {
  model: TaskViewModel
  scrollableModel: ScrollableModel
  messageId?: string | null
}) => {
  const task = props.model.task.get()
  const taskId = task?.id
  const queries = useQueries()
  const persistence = usePersistence()

  const loadMorePressed = useRef(false)
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null)

  const model = useModel(() => {
    return taskId && selectedPageId ? createLLMLogChatModel(taskId, queries, persistence, selectedPageId) : null
  }, [taskId, selectedPageId])

  useEffect(() => {
    if (!model) return

    if (!loadMorePressed.current && !model.disableAutoscroll.get()) {
      props.scrollableModel.scrollToBottom("instant")
    }

    loadMorePressed.current = false
  }, [model, props.scrollableModel])

  if (!task) {
    return null
  }

  // Show pages list if no page is selected
  if (!selectedPageId) {
    return (
      <Container>
        <LLMLogPagesList
          model={props.model}
          scrollableModel={props.scrollableModel}
          onPageSelect={setSelectedPageId}
          selectedPageId={selectedPageId}
          messageId={props.messageId ?? null}
        />
      </Container>
    )
  }

  // Show log entries for selected page
  if (!model) {
    return (
      <Container>
        <Spinner text={ellipsize("Loading")} />
      </Container>
    )
  }

  return (
    <Container>
      <Horizontal alignItems="baseline">
        <SmallActionLink href="#" onClick={(e) => { e.preventDefault(); setSelectedPageId(null) }}>
          ← Back to pages
        </SmallActionLink>

        <Spring />

        {model.isReady.get()
          ? <LoadMoreButton model={model} loadNext={false} loadMorePressed={loadMorePressed} />
          : <Spinner text={ellipsize("Loading")} />
        }
      </Horizontal>

      <Gap height="8px" />

      <ContextLogsSection model={model} loadMorePressed={loadMorePressed} />

      <RegularLogsSection logs={model.logs.get()} />

      <LoadMoreButton model={model} loadNext loadMorePressed={loadMorePressed} />

      {model.isReady.get() && (
        <Footer>
          <FooterTitle>
            Timeline<Spring />{model.logs.get().length > 1 ? `${formatDateTime(model.logs.get()[0].created)}–${formatDateTime(model.logs.get()[model.logs.get().length - 1].created)}` : null}
          </FooterTitle>
          <LLMLogTimeline logs={[...model.contextLogs.get(), ...model.logs.get()]} />
          <Horizontal alignItems="center" gap="8px 24px" wrap="wrap">
            <DevModeSwitch>
              <input type="checkbox" checked={model.disableAutoscroll.get()}
                     onChange={(e) => runInAction(() => model.disableAutoscroll.set(e.target.checked))} />
              Disable autoscroll
            </DevModeSwitch>

            <SmallActionLink href={grafanaLink(props.model.taskId)} target="_blank" rel="noopener noreferrer">
              Logs in Grafana (prod) {Characters.UpRightArrow}
            </SmallActionLink>
            <SmallActionLink href={taskDashboardLink(props.model.taskId)} target="_blank" rel="noopener noreferrer">
              Task Dashboard (prod) {Characters.UpRightArrow}
            </SmallActionLink>
          </Horizontal>
        </Footer>
      )}
    </Container>
  )
})

const LLMLogTimeline = (props: {
  logs: LLMLogModel[]
}) => {
  const { processedEvents } = useMemo(() => {
    if (props.logs.length === 0) return { processedEvents: [] }

    const times = props.logs.map(log => log.created)
    const minTime = Math.min(...times)
    const maxTime = Math.max(...times)

    const processedEvents = props.logs.map((log, index) => {
      const startTime = log.created
      const endTime = index < props.logs.length - 1 ? times[index + 1] : maxTime

      const width = ((endTime - startTime) / (maxTime - minTime)) * 100
      const left = ((startTime - minTime) / (maxTime - minTime)) * 100

      return ({
        log: log,
        isError: log.errorType != null,
        startTime: startTime,
        endTime: endTime,
        width: isNaN(width) ? 0 : width,
        left: isNaN(left) ? 0 : left,
      })
    })

    return { processedEvents }
  }, [props.logs])

  return (
    <Timeline>
      {processedEvents.map(event => (
        <Tooltip
          key={event.log.id}
          content={`${event.log.role} | ${formatDuration(event.endTime - event.startTime)} till the next event`}
        >
          <TimelineEvent
            key={event.log.id}
            left={event.left}
            width={event.width}
            isError={event.isError}
            onClick={() => document.querySelector(`[data-scroll-to="${event.log.id}"]`)?.scrollIntoView()}
          />
        </Tooltip>
      ))}
    </Timeline>
  )
}

const ContextLogsSection = observer((props: { 
  model: LLMLogChatModel
  loadMorePressed: RefObject<boolean>
}) => {
  const [showContext, setShowContext] = useState(false)
  const contextLogs = props.model.contextLogs.get()
  
  if (contextLogs.length === 0 && !props.model.hasContextPrev.get()) {
    return null
  }
  
  return (
    <ContextSection>
      <ContextSectionHeader onClick={() => setShowContext(!showContext)}>
        <ContextSectionToggle expanded={showContext}>
          {showContext ? "▼" : "▶"}
        </ContextSectionToggle>
        <ContextSectionTitle>
          Context ({contextLogs.length} {contextLogs.length === 1 ? "entry" : "entries"})
        </ContextSectionTitle>
      </ContextSectionHeader>
      
      {showContext && (
        <ContextSectionContent>
          <ContextLoadMoreButton 
            model={props.model} 
            loadMorePressed={props.loadMorePressed} 
          />
          
          {contextLogs.map((logEntry, index) => (
            <div key={logEntry.id}>
              <LLMLogEntry log={logEntry} isContext />
              {logEntry.lastInRequest && index < contextLogs.length - 1 && (
                <RequestSeparatorWithStats log={logEntry} />
              )}
            </div>
          ))}
        </ContextSectionContent>
      )}
    </ContextSection>
  )
})

const ContextLoadMoreButton = observer((props: {
  model: LLMLogChatModel
  loadMorePressed: RefObject<boolean>
}) => {
  if (!props.model.hasContextPrev.get()) {
    return null
  }
  
  return (
    <ContextLoadMoreContainer>
      {props.model.isLoadingContextPrev.get() ? (
        <Spinner text={ellipsize("Loading")} />
      ) : (
        <SmallActionLink 
          href="#" 
          onClick={(e) => {
            e.preventDefault()
            props.loadMorePressed.current = true
            props.model.loadContextPrev()
          }}
        >
          Load more context...
        </SmallActionLink>
      )}
    </ContextLoadMoreContainer>
  )
})

const formatRequestStats = (log: LLMLogModel) => {
  const parts: string[] = []
  
  if (log.durationMs) {
    parts.push(formatDuration(log.durationMs))
  }
  
  if (log.tokenStats) {
    const input = log.tokenStats.inputTokens
    const output = log.tokenStats.outputTokens
    if (input >= 1000) {
      parts.push(`${(input / 1000).toFixed(1)}k in`)
    } else {
      parts.push(`${input} in`)
    }
    if (output >= 1000) {
      parts.push(`${(output / 1000).toFixed(1)}k out`)
    } else {
      parts.push(`${output} out`)
    }
  }
  
  if (log.creditsSpent) {
    parts.push(`$${formatDollars(creditsToDollars(log.creditsSpent))}`)
  }
  
  return parts.length > 0 ? parts.join(" · ") : null
}

const RequestSeparatorWithStats = (props: { log: LLMLogModel }) => {
  const stats = useMemo(() => formatRequestStats(props.log), [props.log])
  
  return (
    <RequestSeparator>
      <RequestSeparatorLine />
      <RequestSeparatorContent>
        <RequestSeparatorLabel>LLM Request</RequestSeparatorLabel>
        {stats && <RequestSeparatorStats>{stats}</RequestSeparatorStats>}
      </RequestSeparatorContent>
      <RequestSeparatorLine />
    </RequestSeparator>
  )
}

const RegularLogsSection = (props: { logs: LLMLogModel[] }) => {
  return (
    <>
      {props.logs.map((logEntry, index, array) => (
        <div key={logEntry.id}>
          <LLMLogEntry log={logEntry} />
          {logEntry.lastInRequest && index < array.length - 1 && (
            <RequestSeparatorWithStats log={logEntry} />
          )}
        </div>
      ))}
    </>
  )
}

const LLMLogEntry = observer((props: {
  log: LLMLogModel
  isContext?: boolean
}) => {
  const isMyMessage = props.log.fromDarkmatter
  const [showFullMessage, setShowFullMessage] = useState(false)

  const headerParts = useMemo(() => {
    const parts: string[] = [props.log.role]
    if (props.log.creditsSpent) {
      parts.push(`$${formatDollars(creditsToDollars(props.log.creditsSpent))}`)
    }
    parts.push(formatDateTime(props.log.created))
    return parts.join(" · ")
  }, [props.log.role, props.log.creditsSpent, props.log.created])

  return (
    <UserMessageContainer
      data-scroll-to={props.log.id}
      isMyMessage={isMyMessage}
      isActive
    >
      <StyledUserMessageBalloon
        isMyMessage={isMyMessage}
        className={props.log.errorType ? Warning : undefined}
      >
        <Vertical>
          <Horizontal gap="8px" alignItems="center" justifyContent="space-between">
            <UserMessageType onClick={() => props.log.toggleCollapsed()}>
              {headerParts}
              {props.log.isCachePoint && <CachePointBadge> · Cache point</CachePointBadge>}
            </UserMessageType>
          </Horizontal>

          {!props.log.collapsed.get()
            ? (
              <LogContent log={props.log} showFullMessage={showFullMessage}
                          setShowFullMessage={setShowFullMessage} />
            )
            : null
          }
        </Vertical>
      </StyledUserMessageBalloon>
    </UserMessageContainer>
  )
})

const LogContent = (props: {
  log: LLMLogModel
  showFullMessage: boolean
  setShowFullMessage: (value: boolean) => void
}) => {
  return (
    <>
      <MessageText>
        <RawContent>
          {props.showFullMessage ? props.log.formattedMessage : props.log.shortFormattedMessage}
        </RawContent>
        <ShowFullLogEntryTrigger log={props.log} showFullMessage={props.showFullMessage}
                                 setShowFullMessage={props.setShowFullMessage} />
      </MessageText>

      {props.log.error && (
        <ErrorBlock>
          <RawContent>{props.log.error}</RawContent>
        </ErrorBlock>
      )}
    </>
  )
}

const ShowFullLogEntryTrigger = (props: {
  log: LLMLogModel
  showFullMessage: boolean
  setShowFullMessage: (value: boolean) => void
}) => {
  if (props.log.collapsed.get() || props.log.formattedMessage.trim() == props.log.shortFormattedMessage.trim()) {
    return null
  }

  return (
    <SmallActionLink href="#" onClick={(e) => {
      e.preventDefault()
      props.setShowFullMessage(!props.showFullMessage)
    }}>
      Show {props.showFullMessage ? "less" : "more"}
    </SmallActionLink>
  )
}

function grafanaLink(taskId: string): string {
  return `https://grafana.intellij.net/explore?schemaVersion=1&panes=%7B%22pt9%22:%7B%22datasource%22:%22c5qeutDVk%22,%22queries%22:%5B%7B%22refId%22:%22A%22,%22expr%22:%22%7Bcluster%3D%5C%22eks-eu-west-1%5C%22%2C%20container%3D%5C%22darkmatter%5C%22%7D%20%7C~%20%5C%22${taskId}%5C%22%20%7C%20json%22,%22queryType%22:%22range%22,%22datasource%22:%7B%22type%22:%22loki%22,%22uid%22:%22c5qeutDVk%22%7D,%22editorMode%22:%22code%22%7D%5D,%22range%22:%7B%22from%22:%22now-1h%22,%22to%22:%22now%22%7D,%22panelsState%22:%7B%22logs%22:%7B%22logs%22:%7B%22visualisationType%22:%22logs%22%7D%7D%7D%7D%7D&orgId=1`
}

function taskDashboardLink(taskId: string): string {
  return `https://grafana.intellij.net/d/benxya8w46qkgd/task-dashboard?orgId=1&var-taskId=${taskId}&var-level=.%2A`
}

const Container = styled.div`
  width: 100%;
`

const RawContent = styled.pre`
  ${textStyles.pre}
`

const ErrorBlock = styled.div`
  ${textStyles.smallFontSize};
  ${textStyles.breakWord};

  margin: 8px 0;
  padding: 16px;
  border: 1px solid var(--error-color);
  border-radius: var(--border-radius-6);

  color: var(--text-color);
  background-color: var(--error-color-20);
`

const FooterTitle = styled(Horizontal)`
  ${textStyles.smallestFontSize};

  color: var(--text-color-40);
  white-space: nowrap;

  gap: 8px;
`

const Footer = styled.div`
  position: sticky;
  bottom: 0;
  padding: 8px;
  background: linear-gradient(to bottom, var(--text-color-5), var(--text-color-5)), var(--background-color); /* color mixing hack */
  z-index: 1;
`

const Timeline = styled.div`
  position: relative;
  height: 24px;
  width: 100%;

  margin: 8px 0;
  background: var(--main-color-5);
`

const TimelineEvent = styled.div<{
  left: number
  width: number
  isError: boolean
}>`
  position: absolute;
  min-width: ${props => props.isError ? "3px" : "1px"};
  height: 100%;

  background: ${props => props.isError ? "var(--warning-color-80)" : "var(--main-color-20)"};

  &:hover {
    background: ${props => props.isError ? "var(--warning-color)" : "var(--main-color-50)"};
  }

  left: ${props => `${props.left}%`};
  width: ${props => `${props.width}%`};

  z-index: ${props => props.isError ? "1" : "auto"};
`

const DevModeSwitch = styled.label`
  color: var(--text-color);
  font-size: small;
  font-weight: normal;
  white-space: nowrap;
  user-select: none;

  & > input {
    margin-right: 4px;
  }
`

const SmallActionLink = styled.a`
  ${textStyles.smallFontSize};
  white-space: nowrap;
  color: var(--main-color);
  
  &:hover {
    color: var(--main-color-hover);
  }
`

const CachePointBadge = styled.span`
  color: var(--success-color);
`

const Warning = css`
  background-color: var(--warning-color-10);
`

const RequestSeparator = styled(Horizontal)`
  align-items: center;
  gap: 12px;
  margin: 16px 0;
  padding: 0 8px;
`

const RequestSeparatorLine = styled.div`
  flex: 1;
  height: 1px;
  background: var(--text-color-20);
`

const RequestSeparatorContent = styled(Vertical)`
  align-items: center;
  gap: 2px;
`

const RequestSeparatorLabel = styled.span`
  ${textStyles.smallestFontSize};
  color: var(--text-color-40);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`

const RequestSeparatorStats = styled.span`
  ${textStyles.smallestFontSize};
  color: var(--text-color-40);
  white-space: nowrap;
`

const ContextSection = styled.div`
  margin-bottom: 16px;
  border: 1px solid var(--text-color-10);
  border-radius: var(--border-radius-6);
  background: var(--text-color-5);
`

const ContextSectionHeader = styled(Horizontal)`
  align-items: center;
  gap: 8px;
  padding: 12px;
  cursor: pointer;
  user-select: none;
  
  &:hover {
    background: var(--text-color-10);
    border-radius: var(--border-radius-6);
  }
`

const ContextSectionToggle = styled.span<{ expanded: boolean }>`
  ${textStyles.smallFontSize};
  color: var(--text-color-40);
  width: 16px;
`

const ContextSectionTitle = styled.span`
  ${textStyles.smallFontSize};
  color: var(--text-color-40);
  font-weight: 500;
`

const ContextSectionContent = styled.div`
  padding: 12px;
  border-top: 1px solid var(--text-color-10);
`

const ContextLoadMoreContainer = styled.div`
  padding: 0 0 8px;
  text-align: center;
`

